import type { ControllerRenderProps } from 'react-hook-form';

import { Input } from '@art-of-hospitality-solutions-llc/ui-kit/components/input';
import { Textarea } from '@art-of-hospitality-solutions-llc/ui-kit/components/textarea';
import { Checkbox } from '@art-of-hospitality-solutions-llc/ui-kit/components/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@art-of-hospitality-solutions-llc/ui-kit/components/select';
import {
  RadioGroup,
  RadioGroupItem,
} from '@art-of-hospitality-solutions-llc/ui-kit/components/radio-group';
import { Label } from '@art-of-hospitality-solutions-llc/ui-kit/components/label';

import type { FieldConfig } from './types';

interface DynamicFieldProps {
  config: FieldConfig;
  field: ControllerRenderProps;
}

export function DynamicField({ config, field }: DynamicFieldProps) {
  switch (config.type) {
    case 'text':
    case 'email':
    case 'password':
    case 'date':
    case 'datetime-local':
    case 'time':
      return (
        <Input
          type={config.type}
          placeholder={config.placeholder}
          disabled={config.disabled}
          readOnly={config.readOnly}
          {...field}
          value={(field.value as string) ?? ''}
        />
      );

    case 'textarea':
      return (
        <Textarea
          placeholder={config.placeholder}
          disabled={config.disabled}
          readOnly={config.readOnly}
          className="resize-none"
          {...field}
          value={(field.value as string) ?? ''}
        />
      );

    case 'number':
      return (
        <Input
          type="number"
          placeholder={config.placeholder}
          disabled={config.disabled}
          readOnly={config.readOnly}
          min={config.min}
          max={config.max}
          step={config.step}
          {...field}
          value={field.value === undefined ? '' : String(field.value)}
          onChange={(e) => {
            const val = e.target.value;
            field.onChange(val === '' ? '' : val);
          }}
        />
      );

    case 'select':
      return (
        <Select
          onValueChange={field.onChange}
          value={(field.value as string) ?? ''}
          disabled={config.disabled}
        >
          <SelectTrigger>
            <SelectValue
              placeholder={config.placeholder ?? `Select ${config.label}`}
            />
          </SelectTrigger>
          <SelectContent>
            {config.options?.map((opt) => (
              <SelectItem key={opt.value} value={opt.value}>
                {opt.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      );

    case 'radio':
      return (
        <RadioGroup
          onValueChange={field.onChange}
          value={(field.value as string) ?? ''}
          disabled={config.disabled}
        >
          {config.options?.map((opt) => (
            <div className="flex items-center space-x-2" key={opt.value}>
              <RadioGroupItem
                value={opt.value}
                id={`${field.name}-${opt.value}`}
              />
              <Label htmlFor={`${field.name}-${opt.value}`}>{opt.label}</Label>
            </div>
          ))}
        </RadioGroup>
      );

    case 'checkbox':
      return (
        <div className="flex items-center space-x-2">
          <Checkbox
            checked={(field.value as boolean) ?? false}
            onCheckedChange={field.onChange}
            disabled={config.disabled}
            id={field.name}
          />
          <Label htmlFor={field.name}>{config.label}</Label>
        </div>
      );

    default:
      return (
        <Input
          placeholder={config.placeholder}
          disabled={config.disabled}
          {...field}
          value={(field.value as string) ?? ''}
        />
      );
  }
}
